import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Plus, Trash2, Copy, Check, X, Tag, Power } from 'lucide-react';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const AdminPromoCodesPage = () => {
  const [codes, setCodes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [showForm, setShowForm] = useState(false);

  const [form, setForm] = useState({
    code: '',
    discount_percent: 10,
    description: '',
    max_uses: '',
    expires_at: '',
    active: true,
  });

  const fetchCodes = async () => {
    try {
      const { data } = await axios.get(`${API}/admin/promo-codes`, { withCredentials: true });
      setCodes(data.codes || []);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to load promo codes');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchCodes(); }, []);

  const resetForm = () => {
    setForm({ code: '', discount_percent: 10, description: '', max_uses: '', expires_at: '', active: true });
    setShowForm(false);
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    setCreating(true);
    try {
      const payload = {
        code: form.code.trim().toUpperCase() || null,
        discount_percent: Number(form.discount_percent),
        description: form.description.trim() || null,
        max_uses: form.max_uses ? Number(form.max_uses) : null,
        expires_at: form.expires_at ? new Date(form.expires_at).toISOString() : null,
        active: form.active,
      };
      const { data } = await axios.post(`${API}/admin/promo-codes`, payload, { withCredentials: true });
      setCodes([data, ...codes]);
      resetForm();
      toast.success(`Created ${data.code}`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to create code');
    } finally {
      setCreating(false);
    }
  };

  const toggleActive = async (c) => {
    try {
      const { data } = await axios.patch(`${API}/admin/promo-codes/${c.id}`, { active: !c.active }, { withCredentials: true });
      setCodes(codes.map((x) => (x.id === c.id ? data : x)));
      toast.success(`${data.code} is now ${data.active ? 'active' : 'inactive'}`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Update failed');
    }
  };

  const handleDelete = async (c) => {
    if (!window.confirm(`Permanently delete ${c.code}? This cannot be undone.`)) return;
    try {
      await axios.delete(`${API}/admin/promo-codes/${c.id}`, { withCredentials: true });
      setCodes(codes.filter((x) => x.id !== c.id));
      toast.success('Deleted');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Delete failed');
    }
  };

  const copyCode = (code) => {
    navigator.clipboard.writeText(code);
    toast.success(`${code} copied`);
  };

  const isExpired = (iso) => iso && new Date(iso) < new Date();

  return (
    <div className="min-h-screen bg-[#050505] py-8" data-testid="admin-promo-codes-page">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <Link to="/admin" className="inline-flex items-center space-x-2 text-[#A1A7B5] hover:text-white mb-8 transition-colors">
          <ArrowLeft className="w-5 h-5" />
          <span>Back to Dashboard</span>
        </Link>

        <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3 mb-8">
          <div>
            <h1 className="text-3xl sm:text-4xl font-bold text-white" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Promo Codes
            </h1>
            <p className="text-[#A1A7B5] text-sm mt-1">Create discount codes for customers. Codes never stack with the volume tier — the highest discount per item wins.</p>
          </div>
          <Button
            onClick={() => setShowForm(!showForm)}
            className="btn-primary"
            data-testid="admin-promo-toggle-form"
          >
            <Plus className="w-4 h-4 mr-2" />
            {showForm ? 'Cancel' : 'New Code'}
          </Button>
        </div>

        {/* Create Form */}
        {showForm && (
          <form onSubmit={handleCreate} className="glass-card p-6 mb-8 space-y-4" data-testid="admin-promo-create-form">
            <h2 className="text-lg font-semibold text-white" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Create Promo Code
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-xs text-[#A1A7B5] uppercase tracking-wide mb-2">Code (leave blank to auto-generate)</label>
                <Input
                  value={form.code}
                  onChange={(e) => setForm({ ...form, code: e.target.value.toUpperCase() })}
                  placeholder="e.g. SUMMER20"
                  className="bg-[#111621] border-[#1E2532] text-white uppercase"
                  data-testid="admin-promo-code-input"
                />
              </div>
              <div>
                <label className="block text-xs text-[#A1A7B5] uppercase tracking-wide mb-2">Discount % <span className="text-red-400">*</span></label>
                <Input
                  type="number"
                  min="1"
                  max="100"
                  step="1"
                  required
                  value={form.discount_percent}
                  onChange={(e) => setForm({ ...form, discount_percent: e.target.value })}
                  className="bg-[#111621] border-[#1E2532] text-white"
                  data-testid="admin-promo-discount-input"
                />
              </div>
              <div className="sm:col-span-2">
                <label className="block text-xs text-[#A1A7B5] uppercase tracking-wide mb-2">Description (shown to customer)</label>
                <Input
                  value={form.description}
                  onChange={(e) => setForm({ ...form, description: e.target.value })}
                  placeholder="e.g. Summer sale 20% off"
                  className="bg-[#111621] border-[#1E2532] text-white"
                />
              </div>
              <div>
                <label className="block text-xs text-[#A1A7B5] uppercase tracking-wide mb-2">Max Uses (blank = unlimited)</label>
                <Input
                  type="number"
                  min="1"
                  value={form.max_uses}
                  onChange={(e) => setForm({ ...form, max_uses: e.target.value })}
                  placeholder="Unlimited"
                  className="bg-[#111621] border-[#1E2532] text-white"
                />
              </div>
              <div>
                <label className="block text-xs text-[#A1A7B5] uppercase tracking-wide mb-2">Expires (optional)</label>
                <Input
                  type="date"
                  value={form.expires_at}
                  onChange={(e) => setForm({ ...form, expires_at: e.target.value })}
                  className="bg-[#111621] border-[#1E2532] text-white"
                />
              </div>
            </div>
            <div className="flex items-center gap-3 pt-2">
              <Button type="submit" disabled={creating} className="btn-primary" data-testid="admin-promo-submit">
                {creating ? 'Creating…' : 'Create Code'}
              </Button>
              <Button type="button" onClick={resetForm} className="btn-secondary">Cancel</Button>
            </div>
          </form>
        )}

        {/* List */}
        {loading ? (
          <div className="glass-card p-8 text-center text-[#A1A7B5]">Loading…</div>
        ) : codes.length === 0 ? (
          <div className="glass-card p-12 text-center" data-testid="admin-promo-empty">
            <Tag className="w-12 h-12 text-[#A1A7B5] mx-auto mb-4 opacity-50" />
            <p className="text-white font-medium mb-1">No promo codes yet</p>
            <p className="text-[#A1A7B5] text-sm">Create your first code to start offering discounts.</p>
          </div>
        ) : (
          <div className="glass-card overflow-hidden" data-testid="admin-promo-list">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-[#111621] text-[#A1A7B5] text-xs uppercase tracking-wide">
                    <th className="text-left px-4 py-3">Code</th>
                    <th className="text-left px-4 py-3">Discount</th>
                    <th className="text-left px-4 py-3">Usage</th>
                    <th className="text-left px-4 py-3">Expires</th>
                    <th className="text-left px-4 py-3">Status</th>
                    <th className="text-right px-4 py-3">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-[#1E2532]">
                  {codes.map((c) => {
                    const expired = isExpired(c.expires_at);
                    const exhausted = c.max_uses != null && c.usage_count >= c.max_uses;
                    return (
                      <tr key={c.id} className="hover:bg-[#111621]/50 transition-colors" data-testid={`admin-promo-row-${c.code}`}>
                        <td className="px-4 py-4">
                          <div className="flex items-center gap-2">
                            <span className="text-[#00F0FF] font-mono font-bold">{c.code}</span>
                            <button onClick={() => copyCode(c.code)} className="text-[#A1A7B5] hover:text-white transition-colors" aria-label="Copy">
                              <Copy className="w-3.5 h-3.5" />
                            </button>
                          </div>
                          {c.description && <p className="text-[#A1A7B5] text-xs mt-1">{c.description}</p>}
                        </td>
                        <td className="px-4 py-4 text-white font-semibold">{c.discount_percent}%</td>
                        <td className="px-4 py-4 text-[#A1A7B5]">
                          {c.usage_count}{c.max_uses != null ? ` / ${c.max_uses}` : ' / ∞'}
                        </td>
                        <td className="px-4 py-4 text-[#A1A7B5]">
                          {c.expires_at ? new Date(c.expires_at).toLocaleDateString() : '—'}
                        </td>
                        <td className="px-4 py-4">
                          {!c.active ? (
                            <span className="inline-block px-2 py-0.5 text-xs rounded bg-[#1E2532] text-[#A1A7B5]">Inactive</span>
                          ) : expired ? (
                            <span className="inline-block px-2 py-0.5 text-xs rounded bg-red-500/10 text-red-400">Expired</span>
                          ) : exhausted ? (
                            <span className="inline-block px-2 py-0.5 text-xs rounded bg-orange-500/10 text-orange-400">Used up</span>
                          ) : (
                            <span className="inline-block px-2 py-0.5 text-xs rounded bg-green-500/10 text-green-400">Active</span>
                          )}
                        </td>
                        <td className="px-4 py-4">
                          <div className="flex items-center justify-end gap-2">
                            <button
                              onClick={() => toggleActive(c)}
                              className="text-[#A1A7B5] hover:text-[#00F0FF] transition-colors p-1"
                              aria-label="Toggle active"
                              data-testid={`admin-promo-toggle-${c.code}`}
                              title={c.active ? 'Deactivate' : 'Activate'}
                            >
                              <Power className="w-4 h-4" />
                            </button>
                            <button
                              onClick={() => handleDelete(c)}
                              className="text-[#A1A7B5] hover:text-red-400 transition-colors p-1"
                              aria-label="Delete"
                              data-testid={`admin-promo-delete-${c.code}`}
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminPromoCodesPage;
